import React, {useContext} from 'react'
import {useSelector} from 'react-redux'
import {ReadingGroupsStyles} from './Style'
import {ReadingGroupsContext} from './ReadingGroupsContext'
import {Chip} from 'components/Form'
import {ratingListChipColors, ratingIcons, ratingListLabel10} from 'keys/rating'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import WarningAmberIcon from '@mui/icons-material/WarningAmber'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'

const GROUP_META = {
    excellent: {
        icon: <CheckCircleOutlineIcon fontSize="small"/>,
        paceGood: true,
        accuracyGood: true,
        title: "קוראים בצורה מדויקת ובקצב טוב"
    },

    good_accuracy: {
        icon: <InfoOutlinedIcon fontSize="small"/>,
        paceGood: false,
        accuracyGood: true,
        title: "קוראים בצורה מדויקת ובקצב איטי"

    },
    good_pace: {
        icon: <InfoOutlinedIcon fontSize="small"/>,
        paceGood: true,
        accuracyGood: false,
        title: "קוראים בצורה לא מדוייקת ובקצב טוב"

    },
    both_poor: {
        icon: <WarningAmberIcon fontSize="small"/>,
        paceGood: false,
        accuracyGood: false,
        title: "קושי בדיוק וקושי בקצב"

    },
}

// Grouping rules by group type:
//   good_accuracy (paceGood:false, accuracyGood:true): primary=pace (bad dim), secondary=accuracy
//   all others (excellent, good_pace, both_poor): primary=accuracy, secondary=pace
// Sort direction: ASC for both dimensions — lower bad-rating (3<4<5) = better/first;
//   lower good-rating (1<2) = better/first.
// When !splitRatings and group is "excellent" (both dims good):
//   primary ratings 1&2 collapse into one card (same "cube").
const groupStudentsByRating = (students, paceGood, accuracyGood, splitRatings) => {
    const usePaceAsPrimary = !paceGood && accuracyGood
    const primaryFn = usePaceAsPrimary ? s => s.pace_rating : s => s.accuracy_rating
    const secondaryFn = usePaceAsPrimary ? s => s.accuracy_rating : s => s.pace_rating

    const bothGood = paceGood && accuracyGood
    const getKey = (student) => {
        const val = primaryFn(student)
        if (!splitRatings && bothGood && val <= 2) return 0
        return val
    }

    const sorted = [...students].sort((a, b) => {
        const pa = primaryFn(a), pb = primaryFn(b)
        if (pa !== pb) return pa - pb
        return secondaryFn(a) - secondaryFn(b)
    })

    const groups = []
    const keyToIdx = new Map()
    sorted.forEach(student => {
        const key = getKey(student)
        if (!keyToIdx.has(key)) {
            keyToIdx.set(key, groups.length)
            groups.push([])
        }
        groups[keyToIdx.get(key)].push(student)
    })
    return groups
}

const ColHeaderIcon = ({good}) => good
    ? <i className="fa-regular fa-check" style={{color: "#7FAE1A", fontSize: 12}}/>
    : <i className="fa-regular fa-bell" style={{color: "#FF4D4D", fontSize: 12}}/>

const RATING_HIGH = 1
const RATING_EXPECTED_IDX = 1 // 0-based index for מצופה

const getRatingLabel = (rating, splitRatings) => {
    if (!splitRatings && rating === RATING_HIGH) return "מצופה+"
    return ratingListLabel10[rating - 1] ?? "—"
}
const getRatingChipColor = (rating, splitRatings) => {
    if (!splitRatings && rating === RATING_HIGH) return ratingListChipColors[RATING_EXPECTED_IDX]
    return ratingListChipColors[rating - 1] ?? "gray"
}
const getRatingIcon = (rating, splitRatings) => {
    if (!splitRatings && rating === RATING_HIGH) return ratingIcons[RATING_EXPECTED_IDX]
    return ratingIcons[rating - 1]
}

const ReadingGroups = () => {
    const {brokenDisplay} = useSelector(state => state.general)
    const {
        groups: groupsRes,
        splitRatings,
        toolbarTextTitle,
        toolbarTextSubTitle,
        customToolbar
    } = useContext(ReadingGroupsContext)

    const groups = groupsRes?.data || []

    return (
        <ReadingGroupsStyles>
            {(toolbarTextTitle || toolbarTextSubTitle || customToolbar) && (
                <div className='secondary-toolbar'>
                    <div className='secondary-toolbar-texts'>
                        {toolbarTextTitle && <div className='secondary-toolbar-title'>{toolbarTextTitle}</div>}
                        {toolbarTextSubTitle && <div className='secondary-toolbar-subtitle'>{toolbarTextSubTitle}</div>}
                    </div>
                    {customToolbar}
                </div>
            )}
            <div className='groups-row'>

                {Object.entries(GROUP_META).map(([key, value]) => {
                    const group = groups.find(group => group.key === key)
                    if (!group) return null

                    const {icon, paceGood, accuracyGood} = value

                    return (
                        <div key={key} className='profile-section'>

                            <div className='profile-header'>
                                <div style={{fontWeight: 700}} className='profile-title'>
                                    {icon}
                                    <span>{value.title}</span>
                                </div>
                                <div
                                    className={`group-count`}
                                >
                                    {group.count} תלמידים · {group.percentage}%
                                </div>

                            </div>

                            <div className='student-table'>
                                {groupStudentsByRating(group.students, paceGood, accuracyGood, splitRatings).map((subGroup, groupIdx) => (
                                    <div key={groupIdx} className='student-group-card'>
                                        {groupIdx === 0 && (
                                            <div className='table-header-row'>
                                                <div className='col-name'>תלמיד</div>
                                                <div className='col-rating'><ColHeaderIcon good={accuracyGood}/>דיוק
                                                </div>
                                                <div className='col-rating'><ColHeaderIcon good={paceGood}/>קצב</div>
                                            </div>
                                        )}
                                        {subGroup.map((student) => (
                                            <div
                                                key={student.id}
                                                className={`student-row`}
                                            >
                                                <div className={`col-name  ${brokenDisplay ? 'blurred2' : ''}`}>
                                                    {student.name}
                                                </div>

                                                <div className='col-rating'>
                                                    <Chip
                                                        color={getRatingChipColor(student.accuracy_rating, splitRatings)}
                                                        size="small">
                                                        <i className={getRatingIcon(student.accuracy_rating, splitRatings)}
                                                           style={{fontSize: 11}}/>
                                                        {getRatingLabel(student.accuracy_rating, splitRatings)}
                                                    </Chip>
                                                </div>
                                                <div className='col-rating'>
                                                    <Chip color={getRatingChipColor(student.pace_rating, splitRatings)}
                                                          size="small">
                                                        <i className={getRatingIcon(student.pace_rating, splitRatings)}
                                                           style={{fontSize: 11}}/>
                                                        {getRatingLabel(student.pace_rating, splitRatings)}
                                                    </Chip>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                ))}
                            </div>
                        </div>
                    )
                })}
            </div>
        </ReadingGroupsStyles>
    )
}

export default ReadingGroups
